import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { ipInAnyCidr, parseAllowlistInput } from './cidrValidator'

import styles from './IpAllowlistEditModal.module.scss'

import { getMyCurrentIp } from '@/api/enterprise'
import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import Dialog from '@/components/Dialog'
import TextArea from '@/components/TextArea'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { AppDispatch } from '@/store'
import { updateEnterpriseSettingsThunk } from '@/store/features/user/userSlice'
import { EnterpriseSettings, SettingType } from '@/types'

interface IpAllowlistEditModalProps {
  isOpen: boolean
  close: () => void
  currentSettings: EnterpriseSettings
}

type Phase = 'editing' | 'saving'

const IpAllowlistEditModal = ({ isOpen, close, currentSettings }: IpAllowlistEditModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { updateToast } = useToast()

  const currentList = useMemo(() => currentSettings[SettingType.ipAllowlist] ?? [], [currentSettings])
  const initialText = useMemo(() => currentList.join('\n'), [currentList])

  const [text, setText] = useState(initialText)
  const [observedIp, setObservedIp] = useState<string | null>(null)
  const [ipFetchError, setIpFetchError] = useState(false)
  const [phase, setPhase] = useState<Phase>('editing')
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const wasOpenRef = useRef(false)

  const { cidrs, errors } = useMemo(() => parseAllowlistInput(text), [text])
  const ipInProposed = observedIp !== null && cidrs.length > 0 && ipInAnyCidr(observedIp, cidrs)

  const isDirty = text.trim() !== initialText.trim()

  // Save is gated client-side so the user gets immediate feedback (tooltip on
  // the disabled button) instead of finding out post-click.
  const saveBlockedReason = (() => {
    if (ipFetchError)
      return 'We could not determine your current IP. Save is unavailable until the server is reachable.'
    if (observedIp === null) return 'Detecting your current IP — one moment.'
    if (errors.length > 0) return 'Fix the errors first.'
    if (cidrs.length === 0) return 'Add at least one CIDR.'
    if (!ipInProposed) {
      return `Your current IP (${observedIp}) isn't covered by any range above. Saving now would lock you out of the admin portal.`
    }
    if (!isDirty) return 'No changes to save.'
    return null
  })()
  const canSave = isDirty && saveBlockedReason === null

  // Reinitialize on isOpen false→true.
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!justOpened) return

    setText(initialText)
    setPhase('editing')
    setObservedIp(null)
    setIpFetchError(false)
    setShowDiscardConfirm(false)

    let cancelled = false
    void (async () => {
      try {
        const response = await getMyCurrentIp()
        if (cancelled) return
        if (response.data.ip) setObservedIp(response.data.ip)
        else setIpFetchError(true)
      } catch {
        if (cancelled) return
        setIpFetchError(true)
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Native browser confirm on tab close with unsaved changes.
  useEffect(() => {
    if (!isOpen || !isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isOpen, isDirty])

  const doSave = async () => {
    setPhase('saving')
    try {
      await dispatch(updateEnterpriseSettingsThunk({ settings: { [SettingType.ipAllowlist]: cidrs } })).unwrap()
      updateToast('IP allowlist updated', 'success')
      close()
    } catch (error) {
      updateToast(typeof error === 'string' ? error : 'Failed to update IP allowlist', 'error')
      setPhase('editing')
    }
  }

  const handleClose = () => {
    if (phase === 'saving') return
    if (isDirty) {
      setShowDiscardConfirm(true)
      return
    }
    close()
  }

  const handleDiscard = () => {
    setShowDiscardConfirm(false)
    close()
  }

  const saveButton = (
    <Button
      title="Save"
      onClick={() => void doSave()}
      type="dark"
      isDisabled={!canSave || phase === 'saving'}
      isLoading={phase === 'saving'}
    />
  )

  return (
    <>
      <Dialog isOpen={isOpen && !showDiscardConfirm} title="Edit IP allowlist" close={handleClose} minWidth={520}>
        <div className={styles.modalBody}>
          <p className={styles.helpText}>Enter CIDRs one per line, or comma-separated.</p>

          <div className={styles.ipStatus} data-testid="observed-ip">
            {observedIp !== null && (
              <>
                Your current IP: <code>{observedIp}</code>
              </>
            )}
            {observedIp === null && !ipFetchError && <>Detecting your IP&hellip;</>}
            {ipFetchError && (
              <span className={styles.ipBad}>Could not determine your current IP. Try reopening this dialog.</span>
            )}
          </div>

          <TextArea
            value={text}
            rows={8}
            onInput={setText}
            placeholder={'203.0.113.0/24\n10.0.0.0/8'}
            isDisabled={phase !== 'editing'}
            className={styles.textArea}
          />

          {/* Reserve a fixed slot for parse errors so the modal height stays
              constant whether or not errors are present. */}
          <div className={styles.errorSlot}>
            {errors.length > 0 && (
              <ul className={styles.errorList}>
                {errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.modalActions}>
            <Button title="Cancel" onClick={handleClose} type="light" isDisabled={phase === 'saving'} />
            {saveBlockedReason !== null ? (
              <Tooltip text={saveBlockedReason} placement="top" hideArrow>
                {saveButton}
              </Tooltip>
            ) : (
              saveButton
            )}
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={showDiscardConfirm}
        title="Discard unsaved changes?"
        message="Your edits to the IP allowlist haven't been saved. Discarding will lose them."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        confirmType="destructive"
        onConfirm={handleDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </>
  )
}

export default IpAllowlistEditModal
